import * as QRCode from "qrcode";

// QR codes are always rendered pure black-on-white regardless of studio branding — contrast
// is what makes them scan reliably off a large printed banner. Error-correction is kept at "M":
// enough resilience for print, while keeping the module count (and therefore the density) low.
const QR_OPTIONS = {
  margin: 2,
  errorCorrectionLevel: "M" as const,
  color: {
    dark: "#000000",
    light: "#ffffff",
  },
};

/** Default export resolution for a high-resolution raster download, in pixels. */
export const HIGH_RES_PNG_SIZE = 2048;

/** On-screen preview size, in pixels. */
export const PREVIEW_PNG_SIZE = 220;

/**
 * Renders the QR as a PNG data URL. Used both for the on-screen preview and for the
 * high-resolution download (pass a large `size` for print).
 */
export const generateQrPngDataUrl = (
  value: string,
  size: number = PREVIEW_PNG_SIZE,
): Promise<string> =>
  QRCode.toDataURL(value, { ...QR_OPTIONS, width: size });

/** Renders the QR as scalable SVG markup — the best format for large-format print. */
export const generateQrSvg = (value: string): Promise<string> =>
  QRCode.toString(value, { ...QR_OPTIONS, type: "svg" });

const triggerDownload = (href: string, fileName: string): void => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Turns a QR name into a safe file-name stem (falls back to "qr-kod"). */
export const toFileNameStem = (name: string): string => {
  const cleaned = (name || "")
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return cleaned.length > 0 ? cleaned : "qr-kod";
};

export const downloadQrSvg = async (
  value: string,
  name: string,
): Promise<void> => {
  const svg = await generateQrSvg(value);
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    triggerDownload(url, `${toFileNameStem(name)}.svg`);
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const downloadQrPng = async (
  value: string,
  name: string,
  size: number = HIGH_RES_PNG_SIZE,
): Promise<void> => {
  const dataUrl = await generateQrPngDataUrl(value, size);
  triggerDownload(dataUrl, `${toFileNameStem(name)}.png`);
};
